import React, { useEffect, useState } from "react";
import {
    Box,
    CircularProgress,
    Typography,
    Snackbar,
    Alert,
    useMediaQuery,
    useTheme,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";

const API_URL = window.location.href.includes("localhost")
    ? process.env.REACT_APP_API_URL_LOCAL
    : process.env.REACT_APP_API_URL_PROD;

const FROM_EMAILS = [
    {
        value: "admin@skylinepropertiess.com",
        label: "admin@skylinepropertiess.com – for admin management",
    },
    {
        value: "sales@skylinepropertiess.com",
        label: "📧 sales@skylinepropertiess.com – for property inquiries",
    },
    {
        value: "support@skylinepropertiess.com",
        label: "📧 support@skylinepropertiess.com – for customer support",
    },
    {
        value: "billing@skylinepropertiess.com",
        label: "📧 billing@skylinepropertiess.com – for billing queries",
    },
    {
        value: "info@skylinepropertiess.com",
        label: "📧 info@skylinepropertiess.com – for general inquiries",
    },
    {
        value: "noreply@skylinepropertiess.com",
        label: "📧 noreply@skylinepropertiess.com – system notifications",
    },
];

const EmailSender = () => {
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [rowCount, setRowCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [progressMsg, setProgressMsg] = useState("");


    const [openDialog, setOpenDialog] = useState(false);
    const [emailForm, setEmailForm] = useState({
        fromEmail: FROM_EMAILS[0].value,
        recipients: [],
        subject: "",
        message: "",
    });
    const [sending, setSending] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const fetchClients = async () => {
        try {
            setLoading(true);
            const res = await axios.get(
                `${API_URL}api/get-customers-for-admin?page=${page + 1}&limit=${pageSize}`
            );
            if (res.data.success) {
                setRows(
                    res.data.data.map((item) => {
                        let idVal = item._id;
                        if (idVal && typeof idVal === "object") {
                            idVal = idVal.$oid || idVal.toString();
                        }

                        return {
                            id: idVal,
                            name: item.name || "",
                            email: item.email || "",
                            phone: item.phone || "",
                            age: item.age || "",
                            address: item.address || "",
                            source: (item.source?.toUpperCase().replace("_", " ")) || "",
                            createdAt: item.createdAt,
                        };
                    })
                );
                setRowCount(res.data.total);
            }
        } catch (err) {
            console.error(err);
            setSnackbar({
                open: true,
                message: "Failed to fetch clients",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

const handleSendEmail = async () => {
  try {
    setSending(true);
    const total = emailForm.recipients.length;

    for (let i = 0; i < total; i++) {
      const email = emailForm.recipients[i];
      const client = rows.find(r => r.email === email);

      let personalizedMsg = emailForm.message;
      let personalizedSubject = emailForm.subject;

      if (client && client.name) {
        personalizedMsg = personalizedMsg.replace(/{{name}}/g, client.name);
        personalizedSubject = personalizedSubject.replace(/{{name}}/g, client.name);
      }

      await axios.post("https://skyline-apis.softsphere.io/api/send-email", {
        fromEmail: `Skyline Properties<${emailForm.fromEmail}>`,
        recipients: [email],
        subject: personalizedSubject,
        message: personalizedMsg,
      });

      // ✅ update progress
      setProgressMsg(`${i + 1}/${total} emails sent`);
    }

    setSnackbar({
      open: true,
      message: "All emails sent successfully!",
      severity: "success",
    });
    setOpenDialog(false);
  } catch (err) {
    console.error(err);
    setSnackbar({
      open: true,
      message: "Failed to send emails",
      severity: "error",
    });
  } finally {
    setSending(false);
    setProgressMsg("");
  }
};



    useEffect(() => {
        fetchClients();
    }, [page, pageSize]);

    const openEmailDialog = (emails) => {
        setEmailForm({
            fromEmail: FROM_EMAILS[0].value,
            recipients: emails,
            subject: "",
            message: "",
        });
        setOpenDialog(true);
    };

    const columns = [
        { field: "name", headerName: "Name", flex: 1, minWidth: 120 },
        { field: "email", headerName: "Email", flex: 1.2, minWidth: 160 },
        { field: "phone", headerName: "Phone", flex: 1, minWidth: 140 },
        { field: "age", headerName: "Age", width: 90 },
        { field: "address", headerName: "Address", flex: 1.2, minWidth: 150 },
        { field: "source", headerName: "Source", width: 120 },
        { field: "createdAt", headerName: "Created At", width: 200 },
        {
            field: "actions",
            headerName: "Actions",
            width: 150,
            renderCell: (params) => (
                <Button
                    size="small"
                    variant="contained"
                    onClick={() => openEmailDialog([params.row.email])}
                >
                    Send Email
                </Button>
            ),
        },
    ];

    return (
        <ContainerWrapper>
            <Paper
                elevation={3}
                sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, height: "100%" }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Typography
                        variant={isMobile ? "h5" : "h4"}
                        fontWeight="bold"
                        gutterBottom
                    >
                        Email Marketing Portal
                    </Typography>

                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => openEmailDialog(rows.map((r) => r.email))}
                    >
                        Send Mail to All
                    </Button>
                </Box>

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ height: "75vh", width: "100%", mt: 2 }}>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            pagination
                            paginationMode="server"
                            pageSize={pageSize}
                            rowsPerPageOptions={[10, 20, 50]}
                            rowCount={rowCount}
                            onPageChange={(newPage) => setPage(newPage)}
                            onPageSizeChange={(newSize) => {
                                setPageSize(newSize);
                                setPage(0);
                            }}
                            disableSelectionOnClick
                        />
                    </Box>
                )}

                {/* Email Dialog */}
                <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
                    <DialogTitle>Send Email</DialogTitle>
                    <DialogContent dividers>
                        <TextField
                            select
                            label="From Email"
                            value={emailForm.fromEmail}
                            onChange={(e) =>
                                setEmailForm({ ...emailForm, fromEmail: e.target.value })
                            }
                            fullWidth
                            margin="normal"
                        >
                            {FROM_EMAILS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            label="Recipients"
                            value={emailForm.recipients.join(", ")}
                            fullWidth
                            margin="normal"
                            disabled
                        />

                        <TextField
                            label="Subject"
                            value={emailForm.subject}
                            onChange={(e) =>
                                setEmailForm({ ...emailForm, subject: e.target.value })
                            }
                            fullWidth
                            margin="normal"
                        />

                        <TextField
                            label="Message (HTML allowed)"
                            value={emailForm.message}
                            onChange={(e) =>
                                setEmailForm({ ...emailForm, message: e.target.value })
                            }
                            fullWidth
                            margin="normal"
                            multiline
                            rows={6}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                        <Button
                            variant="contained"
                            onClick={handleSendEmail}
                            disabled={sending}
                        >
                            {sending ? <CircularProgress size={24} /> : "Send"}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={3000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    <Alert severity={snackbar.severity} variant="filled">
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Paper>
        </ContainerWrapper>
    );
};

const ContainerWrapper = ({ children }) => (
    <Box
        sx={{
            width: "100%",
            p: { xs: 1, sm: 3 },
            mt: { xs: 2, sm: 3 },
            display: "flex",
            justifyContent: "center",
        }}
    >
        <Box sx={{ width: "100%", maxWidth: "1200px" }}>{children}</Box>
    </Box>
);

export default EmailSender;
